import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import {
  ShieldCheck,
  CheckCircle2,
  XCircle,
  Clock,
  FileText,
  Loader2,
  ExternalLink,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * AdminVerifications — admin panel tab.
 *
 * Lists provider ID submissions and lets the admin approve or reject.
 * The document URL is admin-gated, so we open it via a token-authed fetch
 * → blob → object URL so the browser can render it in a new tab.
 */
export default function AdminVerifications() {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/escrow/verifications`, {
        params: filter === 'all' ? {} : { status: filter },
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(res.data.verifications || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [token, filter]);

  useEffect(() => {
    load();
  }, [load]);

  const decide = async (id, approve) => {
    let notes = '';
    if (!approve) {
      notes = window.prompt('Reason for rejection? (shown to the user)') || '';
    }
    setActingId(id);
    try {
      await axios.post(
        `${API_URL}/api/admin/escrow/verifications/${id}/review`,
        { approve, notes },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(approve ? 'Approved' : 'Rejected');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    } finally {
      setActingId(null);
    }
  };

  const openDocument = async (url) => {
    try {
      const res = await axios.get(`${API_URL}${url}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const blobUrl = URL.createObjectURL(res.data);
      window.open(blobUrl, '_blank', 'noopener');
    } catch (err) {
      toast.error('Could not load document');
    }
  };

  const statusBadge = (status) => {
    const base = 'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium';
    switch (status) {
      case 'approved':
        return (
          <span className={`${base} bg-emerald-100 text-emerald-700`}>
            <CheckCircle2 size={12} /> approved
          </span>
        );
      case 'rejected':
        return (
          <span className={`${base} bg-rose-100 text-rose-700`}>
            <XCircle size={12} /> rejected
          </span>
        );
      default:
        return (
          <span className={`${base} bg-amber-100 text-amber-700`}>
            <Clock size={12} /> pending
          </span>
        );
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-verifications">
      <div className="flex items-center gap-3">
        <ShieldCheck className="text-indigo-600" size={20} />
        <h2 className="text-lg font-semibold text-slate-900">Provider Verifications</h2>
        <div className="ml-auto flex gap-1 bg-slate-100 p-1 rounded-lg">
          {['pending', 'approved', 'rejected', 'all'].map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`px-3 py-1 text-xs rounded-md capitalize ${
                filter === s ? 'bg-white shadow text-slate-900' : 'text-slate-500'
              }`}
              data-testid={`verifications-filter-${s}`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-10 text-slate-400">
          <Loader2 className="inline animate-spin mr-2" size={16} /> Loading…
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-10 text-slate-400">No {filter} submissions.</div>
      ) : (
        <div className="space-y-3">
          {items.map((v) => (
            <div
              key={v.id}
              className="border border-slate-200 rounded-xl p-4 flex flex-wrap items-center gap-3 bg-white"
              data-testid={`verification-${v.id}`}
            >
              <div className="flex-1 min-w-[200px]">
                <div className="flex items-center gap-2">
                  <p className="font-medium text-slate-900">{v.user_name || 'Unknown'}</p>
                  {statusBadge(v.status)}
                </div>
                <p className="text-xs text-slate-500">{v.user_email}</p>
                <p className="text-[11px] text-slate-400 mt-1">
                  submitted {new Date(v.submitted_at).toLocaleString()}
                  {v.reviewed_at && ` · reviewed ${new Date(v.reviewed_at).toLocaleString()}`}
                </p>
                {v.notes && (
                  <p className="text-xs text-slate-600 mt-1 italic">notes: {v.notes}</p>
                )}
              </div>
              <button
                onClick={() => openDocument(v.document_url)}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-slate-100 hover:bg-slate-200 rounded-lg"
                data-testid={`verification-view-${v.id}`}
              >
                <FileText size={14} /> view ID
                <ExternalLink size={11} />
              </button>
              {v.status === 'pending' && (
                <>
                  <button
                    disabled={actingId === v.id}
                    onClick={() => decide(v.id, true)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-emerald-600 text-white hover:bg-emerald-700 rounded-lg disabled:opacity-50"
                    data-testid={`verification-approve-${v.id}`}
                  >
                    <CheckCircle2 size={14} /> approve
                  </button>
                  <button
                    disabled={actingId === v.id}
                    onClick={() => decide(v.id, false)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-rose-100 text-rose-700 hover:bg-rose-200 rounded-lg disabled:opacity-50"
                    data-testid={`verification-reject-${v.id}`}
                  >
                    <XCircle size={14} /> reject
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
